import dayjs from "dayjs";
import updateLocale from "dayjs/plugin/updateLocale";
import weekday from "dayjs/plugin/weekday";
import localeData from "dayjs/plugin/localeData";
import isLeapYear from "dayjs/plugin/isLeapYear";
import isoWeek from "dayjs/plugin/isoWeek";
import isoWeeksInYear from "dayjs/plugin/isoWeeksInYear";
import advancedFormat from "dayjs/plugin/advancedFormat";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import minMax from "dayjs/plugin/minMax";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";
import moment from "moment-timezone";
import { loadDateLocale } from "@/src/shared/utils/dateLocales";

/**
 * Initial setup for day.js and moment.js depends on global date settings
 * When weekStart is -1 and locale is EN -> return 0
 * When weekStart is -1 and locale isn't EN -> return default weekStart value for specific locale
 * Otherwise, return weekStart value
 * @param locale
 * @param weekStart
 */
export const initDateLibs = async (locale = "en", weekStart: number) => {
  initDayjsPlugins();
  const lowerCaseLocale = locale.toLowerCase();

  const dayJsLocale = await loadDateLocale(lowerCaseLocale);

  const firstDay = getFirstDayOfWeek(dayJsLocale, weekStart);

  initDayJsLocale(dayJsLocale, lowerCaseLocale, firstDay);
  initMomentLocale(lowerCaseLocale, firstDay);
};

const initDayjsPlugins = () => {
  dayjs.extend(weekday);
  dayjs.extend(updateLocale);
  dayjs.extend(localeData);
  dayjs.extend(isLeapYear);
  dayjs.extend(isoWeek);
  dayjs.extend(isoWeeksInYear);
  dayjs.extend(advancedFormat);
  dayjs.extend(isSameOrAfter);
  dayjs.extend(isSameOrBefore);
  dayjs.extend(minMax);
  dayjs.extend(utc);
  dayjs.extend(customParseFormat);
};

const getFirstDayOfWeek = (dayJsLocale: ILocale | null, weekStart: number): number => {
  if (weekStart === -1) {
    return dayJsLocale?.weekStart ? dayJsLocale.weekStart : 0;
  }
  return weekStart;
};

const initDayJsLocale = (dayJsLocale: ILocale | null, lowerCaseLocale: string, firstDay: number) => {
  const dayjsData = { ...dayJsLocale, weekStart: firstDay, week: { dow: firstDay } };
  dayjs.updateLocale(lowerCaseLocale, dayjsData);

  dayjs.locale(lowerCaseLocale);
};

const initMomentLocale = (lowerCaseLocale: string, firstDay: number) => {
  moment.locale(lowerCaseLocale);

  moment.updateLocale(lowerCaseLocale, {
    week: {
      dow: firstDay,
    },
  });
};
